import axios, { AxiosRequestConfig, CancelTokenSource } from "axios";
import router from "@/router";
import { ClientProject } from "@/models/ClientProject";
import { ContactForm, LeadClientEditViewModel, LeadEditViewModel, LeadsPageViewModel, UpdateLeadResponse, PhonePopupData } from "@/models/leads/ContactForm";
import { DataSourceResult } from "@/models/DataSourceResult";
import { ExplanationPopupData, ExplanationRequestData, ExplanationSendEmailRequest, ExplanationSaveImageFileRequest } from "@/models/ExplanationPopupData";
import { GridColumn, GridColumnExportSetting } from "@/models/GridColumn";
import {LeadsPageKind} from "@/models/LeadsPageKind";
import { AdTitlesByLeads, Assignee, ChannelMultiselectItem, MultiselectItem, SelectListItem } from "@/models/MultiselectItem";
import { Result } from "@/models/Result";
import { TableSettingsResponse, TableSettingsSaveRequestData } from "@/models/TableSettings";
import { UserData } from "@/models/UserData";
import { ChannelMetadataType } from "@/models/campaigns/ChannelMetadataType";
import { LeadNote } from "@/models/leads/LeadNote";
import { LeadStatistic, PhoneLeadStats } from "@/models/leads/LeadStatistic";
import { PhoneStatus } from "@/models/phones/PhoneWithChannel";
import ConfigService from "../ConfigService";
import HelperService from "../HelperService";

export const requestConfig: AxiosRequestConfig = {
  withCredentials: true,
};

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error instanceof Error) {
      if ((error as Error).message === "Network Error") {
        HelperService.showError(`${(error as Error).message} | ${(error as any).config?.url}`);
        return false;
      }
    }
    return Promise.reject(error);
  }
);

export default {
  addNoteToLead(leadId: number, note: string): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.addNoteUrl);
      const leadNote: LeadNote = {
        LeadID: leadId,
        Note: note,
        ID: 0,
        CreatedDate: new Date(),
        GpsLatitude: "",
        GpsLongitude: ""
      };
      axios
        .post(url.toString(), leadNote, requestConfig)
        .then((response) => {
          resolve({
            Data: response.data.leadNote,
            Message: response.data.msg,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  deleteLead(id: number): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.deleteLeadUrl);
      const config = {
        ...requestConfig,
        params: {
          ID: id
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve({
            Message: response.data.msg,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  deleteLeadNote(noteId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.deleteNoteUrl);
      const config = {
        ...requestConfig,
        params: {
          id: noteId
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          if (response.data.success) {
            resolve();
          } else {
            reject(response.data.msg);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  exportLeadsToExcel(leads: DataSourceResult, columns: GridColumn[], kind : LeadsPageKind): Promise<string> {
    const columnsToExport: GridColumnExportSetting[] = columns.map((i: GridColumn) => {
      return {
        Field: i.name,
        Forbidden: i.forbidden,
        Width: i.width ? i.width.toString() : "",
        Hidden: !i.visible,
      };
    });
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.exportLeadsToExcelUrl);
      axios
        .post(url.toString(), { Columns: columnsToExport, Leads: JSON.parse(JSON.stringify(leads)).Data, ShowLeadClients: kind == LeadsPageKind.LeadClients ? "true" : "false" }, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
    },

    exportLeadsToExcelToMail(leadsPayload: LeadsPayload, columns: GridColumn[], kind: LeadsPageKind): Promise<string> {
        const columnsToExport: GridColumnExportSetting[] = columns.map((i: GridColumn) => {
            return {
                Field: i.name,
                Forbidden: i.forbidden,
                Width: i.width ? i.width.toString() : "",
                Hidden: !i.visible,
            };
        });
        return new Promise((resolve, reject) => {
            const url = new URL(ConfigService.exportLeadsToExcelToMailUrl);
               
            if (leadsPayload.filter) {
                url.searchParams.append("filter", leadsPayload.filter);
            }
            if (leadsPayload.group) {
                url.searchParams.append("group", leadsPayload.group);
            }
            if (leadsPayload.page) {
                url.searchParams.append("page", leadsPayload.page.toString());
            }
            if (leadsPayload.pageSize) {
                url.searchParams.append("pageSize", leadsPayload.pageSize.toString());
            }
            if (leadsPayload.sort) {
                url.searchParams.append("sort", leadsPayload.sort);
            }

            axios
                .post(url.toString(), { Columns: columnsToExport, ShowLeadClients: kind == LeadsPageKind.LeadClients ? "true" : "false", LeadsPayload: leadsPayload }, requestConfig)
                .then((response) => {
                    const result: Result = response.data;
                    if (result.Success) {
                        resolve(result.Data);
                    } else {
                        reject(result.Message);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

  getAdGroups(payload: CommonMultiselectPayload): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.adGroupsUrl);
      url.searchParams.append("clientIds", JSON.stringify(payload.clientIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAdPreviewData(adId: number, adGroupId: number, channelId: number): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.getAdPreviewDataUrl);
      const config = {
        ...requestConfig,
        params: {
          aid: adId,
          agroupId: adGroupId,
          channelid: channelId
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAdTitles(payload: CommonMultiselectPayload): Promise<AdTitlesByLeads[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.adTitlesUrl);
      url.searchParams.append("clientIds", JSON.stringify(payload.clientIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAdditionalStatuses(payload: CommonMultiselectPayload): Promise<SelectListItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.additionalStatusesUrl);
      url.searchParams.append("clientIds", JSON.stringify(payload.clientIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAssets(payload: CommonMultiselectPayload): Promise<ClientProject[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.assetsUrl);
      url.searchParams.append("clientIds", JSON.stringify(payload.clientIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAssignees(payload: CommonMultiselectPayload): Promise<Assignee[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.assigneesUrl);
      url.searchParams.append("clientIds", JSON.stringify(payload.clientIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data.Data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAssigneesForClient(payload: AssigneesForClientPayload): Promise<Assignee[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.assigneesUrl);
      if (payload.clientId) {
        url.searchParams.append("clientId", payload.clientId.toString());
      }
      if (payload.assigneeId) {
        url.searchParams.append("id", payload.assigneeId.toString());
      }
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data.Data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getChannels(payload: CommonMultiselectPayload): Promise<ChannelMultiselectItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.channelsUrl);
      url.searchParams.append("clientIds", JSON.stringify(payload.clientIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getClients(): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${ConfigService.clientsUrl}?addShared=True`, null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getProjects(payload: CommonMultiselectPayload): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.projectsUrl);
      url.searchParams.append("clientIds", JSON.stringify(payload.clientIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getDataWithLeadsPayload(payload: LeadsPayload, urlString: string, cancelTokenSource?: CancelTokenSource): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = new URL(urlString);
      if (payload.clientId) {
        url.searchParams.append("сlientId", payload.clientId.toString());
      }
      if (payload.dateFrom) {
        url.searchParams.append("DateFrom", payload.dateFrom.toString());
      }
      if (payload.datePeriod) {
        url.searchParams.append("DatePeriod", payload.datePeriod);
      }
      if (payload.dateTo) {
        url.searchParams.append("DateTo", payload.dateTo.toString());
      }
      if (payload.showDeleted !== undefined) {
        url.searchParams.append("showDeleted", payload.showDeleted ? "true" : "false");
      }
      if (payload.filter) {
        url.searchParams.append("filter", payload.filter);
      }
      if (payload.group) {
        url.searchParams.append("group", payload.group);
      }
      if (payload.page) {
        url.searchParams.append("page", payload.page.toString());
      }
      if (payload.pageSize) {
        url.searchParams.append("pageSize", payload.pageSize.toString());
      }
      if (payload.priceType) {
        url.searchParams.append("priceType", payload.priceType);
      }
      if (payload.sort) {
        url.searchParams.append("sort", payload.sort);
      }
      if (payload.token) {
        url.searchParams.append("token", payload.token);
      }
      if (payload.unique !== undefined) {
        url.searchParams.append("unique", payload.unique ? "true" : "false");
      }
      if (payload.kind !== undefined) {
        url.searchParams.append("showleadClients", payload.kind == LeadsPageKind.LeadClients  ? "true" : "false");
      }
      if (payload.exportFlag) {
        url.searchParams.append("exportFlag", payload.exportFlag.toString());
      }
      const config = {
        ...requestConfig,
        cancelToken: cancelTokenSource?.token,
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            reject(error);
          }
        });
    });
  },

  getDataWithLeadsStatisticByCampaignsPayload(payload: LeadsStatisticByCampaignsPayload, urlString: string, cancelTokenSource?: CancelTokenSource): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(urlString);
      if (payload.clientId) {
        url.searchParams.append("сlientId", payload.clientId.toString());
      }
      if (payload.clientSelected) {
        url.searchParams.append("clientSelected", payload.clientSelected.toString());
      }
      if (payload.dateFrom) {
        url.searchParams.append("dateFrom", payload.dateFrom.toString());
      }
      if (payload.dateTo) {
        url.searchParams.append("dateTo", payload.dateTo.toString());
      }
      if (payload.filter) {
        url.searchParams.append("filter", payload.filter);
      }
      if (payload.token) {
        url.searchParams.append("token", payload.token);
      }
      const config = {
        ...requestConfig,
        cancelToken: cancelTokenSource?.token,
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            reject(error);
          }
        });
    });
  },

  getDataWithLeadsStatisticByPhonesPayload(payload: LeadsStatisticPhonesPayload, urlString: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = new URL(urlString);
      if (payload.dateFrom) {
        url.searchParams.append("dateFrom", payload.dateFrom.toString());
      }
      if (payload.dateTo) {
        url.searchParams.append("dateTo", payload.dateTo.toString());
      }
      if (payload.filter) {
        url.searchParams.append("filter", payload.filter);
      }
      if (payload.phoneStatus) {
        url.searchParams.append("phoneStatus", payload.phoneStatus.toString());
      }
      if (payload.token) {
        url.searchParams.append("token", payload.token);
      }
      if (payload.channelMetadataTypes) {
        payload.channelMetadataTypes.forEach((i: ChannelMetadataType) => {
          url.searchParams.append("channelMetadataTypes", i.toString());
        })
      }

      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getExplanationPopup(): Promise<ExplanationPopupData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.explanationPopupUrl);
      const requestData: ExplanationRequestData = {
        url: location.href
      };
      if (typeof((window as any).fixdigital_explanation) !== undefined) { 
        const explanation: ExplanationRequestData = (window as any).fixdigital_explanation;
        if (explanation && explanation.username) {
          requestData.username = explanation.username;
        }
        if (explanation && explanation.locale) {
          requestData.locale = explanation.locale;
        }
      }
      const config = {
        ...requestConfig,
        params: {
          locale: requestData.locale,
          url: requestData.url,
          username: requestData.username,
        }
      };
      axios
        .post(url.toString(), null, config).then((response) => {
          if (!response?.data) {
            reject(`Wrong response for getExplanationPopup: ${JSON.stringify(response)}`);
          }
          resolve(response.data);
        }).catch((error) => {
          reject(error);
        });
    });
  },

  getLeadClientEditViewModel(id: number): Promise<LeadClientEditViewModel> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.leadClientUrl);
      const config = {
        ...requestConfig,
        params: {
          id
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            switch (result.Message) {
              case "404":
                router.push({ name: "NotFound"});
                break;
              default:
                reject(result.Message);
                break;
            }
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getLeadEditViewModel(id: number): Promise<LeadEditViewModel> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.leadUrl);
      const config = {
        ...requestConfig,
        params: {
          id
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            switch (result.Message) {
              case "404":
                router.push({ name: "NotFound"});
                break;
              default:
                reject(result.Message);
                break;
            }
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getLeads(payload: LeadsPayload, cancelTokenSource?: CancelTokenSource): Promise<DataSourceResult> {
    return this.getDataWithLeadsPayload(payload, ConfigService.contactFormsUrl, cancelTokenSource);
  },

  getLeadsPageData(): Promise<LeadsPageViewModel> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.leadsPageDataUrl);
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

    checkPhonePopup(): Promise<PhonePopupData> {
        const url = new URL(ConfigService.checkPhonePopupUrl);

        return axios
            .get(url.toString())
            .then((response) => {
                const result = response.data;
                if (result && typeof result.showPopup === "boolean") {
                    return result;
                } else {
                    return Promise.reject("Invalid server response.");
                }
            })
            .catch((error) => {
                console.error("Error in CheckPhonePopup:", error);
                return Promise.reject(error);
            });
    },

    setHidePopupCookie(): Promise<void> {
        const url = new URL(ConfigService.setHidePopupCookieUrl);

        return axios
            .post(url.toString())
            .then((response) => {
                if (response.status === 200) {
                    return Promise.resolve();
                } else {
                    return Promise.reject("Failed to set hide popup cookie.");
                }
            })
            .catch((error) => {
                console.error("Error in SetHidePopupCookie:", error);
                return Promise.reject(error);
            });
    },


  getLeadsStatisticByAdditionalStatus(payload: LeadsPayload, cancelTokenSource?: CancelTokenSource): Promise<LeadStatistic[]> {
    return this.getDataWithLeadsPayload(payload, ConfigService.leadsStatisticByAdditionalStatusUrl, cancelTokenSource);
  },

  getLeadsStatisticByCallStatus(payload: LeadsPayload, cancelTokenSource?: CancelTokenSource): Promise<LeadStatistic[]> {
    return this.getDataWithLeadsPayload(payload, ConfigService.leadsStatisticByCallStatusUrl, cancelTokenSource);
  },

  getLeadsStatisticByCampaigns(payload: LeadsStatisticByCampaignsPayload, cancelTokenSource?: CancelTokenSource): Promise<LeadStatistic[]> {
    return this.getDataWithLeadsStatisticByCampaignsPayload(payload, ConfigService.leadsStatisticByCampaignsUrl, cancelTokenSource);
  },

  getLeadsStatisticByPhoneAndForms(payload: LeadsPayload, cancelTokenSource?: CancelTokenSource): Promise<LeadStatistic[]> {
    return this.getDataWithLeadsPayload(payload, ConfigService.leadsStatisticByPhoneAndFormsUrl, cancelTokenSource);
  },

  getLeadsStatisticBySaleStatus(payload: LeadsPayload, cancelTokenSource?: CancelTokenSource): Promise<LeadStatistic[]> {
    return this.getDataWithLeadsPayload(payload, ConfigService.leadsStatisticBySaleStatusUrl, cancelTokenSource);
  },

  getLeadsStatisticChannelMetadataType(payload: LeadsPayload, cancelTokenSource?: CancelTokenSource): Promise<LeadStatistic[]> {
    return this.getDataWithLeadsPayload(payload, ConfigService.leadsStatisticChannelMetadataTypeUrl, cancelTokenSource);
  },

  getLeadsStatisticDealMeetingsLeads(payload: LeadsPayload, cancelTokenSource?: CancelTokenSource): Promise<LeadStatistic[]> {
    return this.getDataWithLeadsPayload(payload, ConfigService.leadsStatisticDealMeetingsLeadsUrl, cancelTokenSource);
  },

  getLeadsStatisticPhones(payload: LeadsStatisticPhonesPayload): Promise<PhoneLeadStats> {
    return this.getDataWithLeadsStatisticByPhonesPayload(payload, ConfigService.leadsStatisticCallsByDateUrl);
  },

  getLeadHistory(leadClientID: number, pageKind: LeadsPageKind = LeadsPageKind.Leads): Promise<DataSourceResult> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.leadHistoryUrl);
      url.searchParams.append("id", leadClientID.toString());
      url.searchParams.append("pageKind", pageKind.toString());
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getTableSettings(culture: string, pageUrl: string): Promise<TableSettingsResponse> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.tableSettingsUrl);
      url.searchParams.append("culture", culture);
      url.searchParams.append("url", pageUrl);
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getTags(payload: CommonMultiselectPayload): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.tagsUrl);
      url.searchParams.append("clientIds", JSON.stringify(payload.clientIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getUserData(): Promise<UserData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.userDataUrl);
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          if (!response?.data) {
            reject(`Wrong response for getUserData: ${JSON.stringify(response)}`);
          }

          if (typeof(response.data) === "string") {
            // if user not authenticated and returned login page html, redirect to login
            if (response.data.indexOf('sign-up-link') >= 0) {
              location.href = `${ConfigService.loginUrl}?returnUrl=${location.pathname}`;
            } else {
              location.href = response.request.responseURL;
            }
          }
          
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
          location.href = `${ConfigService.loginUrl}?returnUrl=${location.pathname}`;
        });
    });
  },

  logout(): Promise<void> {
    return new Promise((resolve, reject) => {
      axios
        .get(ConfigService.logoutUrl, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve();
          } else {
            HelperService.showError(result.Message);
            reject(result.Message);
          }
        })
        .catch((error) => {
          HelperService.showError(error);
          reject(error);
        });
    });
  },

  saveExplanationModuleImage(data: ExplanationSaveImageFileRequest): Promise<Result>{
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.explanationSaveImageFile);
      var formData = new FormData();
      formData.append("image", data.image);
      formData.append("imagename", data.imagename);
      requestConfig.maxContentLength = Infinity;
      requestConfig.maxBodyLength = Infinity;
      axios
        .post(url.toString(), formData, requestConfig).then((response) => {
          resolve({
            Data : response.data.url,
            Message: response.data.message,
            Success: response.data.success
          });
        }).catch((error) => {
          reject(error);
        });
    });
  },

  sendExplanationModuleEmail(data: ExplanationSendEmailRequest): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.explanationSendEmailUrl);
      url.searchParams.append("email", data.email);
      url.searchParams.append("message", data.message);
      url.searchParams.append("name", data.name);
      url.searchParams.append("phone", data.phone);
      url.searchParams.append("url", data.url);
      url.searchParams.append("username", data.username);
      url.searchParams.append("subject", data.subject);
      axios
        .post(url.toString(), null, requestConfig).then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        }).catch((error) => {
          reject(error);
        });
    });
    },

    sendExplanationModuleAlgoliaPost(subject: string): Promise<Result> {
        return new Promise((resolve, reject) => {
            const url = new URL(ConfigService.explanationSendAlgoliaPost);
            url.searchParams.append("subject", subject);
            axios
                .post(url.toString(), null, requestConfig).then((response) => {
                    resolve({
                        Data: response.data.result,
                        Message: response.data.message,
                        Success: response.data.success
                    });
                }).catch((error) => {
                    reject(error);
                });
        });
    },

  setLanguage(culture: string): Promise<UserData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.setLanguageUrl);
      const config = {
        ...requestConfig,
        params: {
          culture,
          returnUrl: location.pathname
        }
      };
      axios
        .post(url.toString(), null, config)
        .finally(() => {
          location.reload();
        });
    });
  },

  saveTableSettings(data: TableSettingsSaveRequestData): Promise<Result> {
    return new Promise((resolve, reject) => {
      var formData = new FormData();
      formData.append("culture", data.culture);
      formData.append("gridId", data.gridId);
      formData.append("optionsJson", data.optionsJson);
      formData.append("url", data.url);
      const url = new URL(ConfigService.saveTableSettingsUrl);
      axios
        .post(url.toString(), formData, requestConfig)
        .then((response) => {
          resolve({
            Message: "",
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  updateLead(lead: ContactForm): Promise<UpdateLeadResponse> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.updateLeadUrl);
      axios
        .post(url.toString(), lead, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  updateLeadClient(leadClient: ContactForm): Promise<UpdateLeadResponse> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.updateLeadClientUrl);
      axios
        .post(url.toString(), leadClient, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};

export interface AssigneesForClientPayload {
  assigneeId?: number;
  clientId?: number;
}

export interface CommonMultiselectPayload {
  clientIds: number[];
  showNotActiveCamp: boolean;
  metadataIds: number[];
  statusIds: string[];
  channelIds: number[];
}

export interface LeadsPayload {
  clientId?: number;
  dateFrom?: string;
  datePeriod?: string;
  dateTo?: string;
  filter?: string;
  group?: string;
  kind?: LeadsPageKind;
  page?: number;
  pageSize: number;
  priceType?: string;
  showDeleted?: boolean;
  sort?: string;
  token?: string;
  unique?: boolean;
  exportFlag?: boolean;
}

export interface LeadsStatisticPhonesPayload {
  dateFrom?: string;
  dateTo?: string;
  filter?: string;
  pageSize: number;
  phoneStatus: PhoneStatus;
  token?: string;
  channelMetadataTypes?: ChannelMetadataType[];
}

export interface LeadsStatisticByCampaignsPayload {
  clientId?: number;
  clientSelected?: boolean;
  dateFrom?: string;
  dateTo?: string;
  filter?: string;
  token?: string;
}
